import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, Stack,
  TableContainer, IconButton, Tooltip,
} from '@mui/material';
import {
  AccessTime, PlayArrow, Stop, AttachMoney, Warning, Refresh,
} from '@mui/icons-material';
import { openShift, closeShift, getCurrentShift, addCashDrop, fetchShifts } from '../../api/api';
import { AuthContext } from '../../context/AuthContext';

const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

export default function ShiftManagement() {
  const { user } = useContext(AuthContext);

  const [currentShift, setCurrentShift] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* open shift dialog */
  const [openShiftDialogOpen, setOpenShiftDialogOpen] = useState(false);
  const [openFloat, setOpenFloat] = useState('');
  const [openLoading, setOpenLoading] = useState(false);
  const [openError, setOpenError] = useState('');

  /* close shift dialog */
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closingFloat, setClosingFloat] = useState('');
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState('');

  /* cash drop dialog */
  const [dropDialogOpen, setDropDialogOpen] = useState(false);
  const [dropAmount, setDropAmount] = useState('');
  const [dropNote, setDropNote] = useState('');
  const [dropLoading, setDropLoading] = useState(false);
  const [dropError, setDropError] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [shiftRes, histRes] = await Promise.all([
        getCurrentShift().catch(() => ({ data: null })),
        fetchShifts().catch(() => ({ data: [] })),
      ]);
      setCurrentShift(shiftRes.data?.shift || shiftRes.data || null);
      const h = histRes.data?.shifts || histRes.data || [];
      setShifts(Array.isArray(h) ? h : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load shift data.');
    } finally {
      setLoading(false);
    }
  };

  /* ── open shift ── */
  const handleOpenShift = async () => {
    if (!openFloat || Number(openFloat) < 0) { setOpenError('Enter a valid opening float.'); return; }
    try {
      setOpenLoading(true);
      setOpenError('');
      await openShift({ openingFloat: Number(openFloat) });
      setOpenShiftDialogOpen(false);
      setOpenFloat('');
      await loadAll();
    } catch (err) {
      setOpenError(err.response?.data?.message || 'Failed to open shift.');
    } finally {
      setOpenLoading(false);
    }
  };

  /* ── close shift ── */
  const expectedFloat = currentShift
    ? (currentShift.openingFloat || 0) + (currentShift.summary?.cashSales || 0) - (currentShift.cashDrops?.reduce((s, d) => s + d.amount, 0) || 0)
    : 0;

  const variance = closingFloat ? Number(closingFloat) - expectedFloat : 0;

  const handleCloseShift = async () => {
    if (!closingFloat || Number(closingFloat) < 0) { setCloseError('Enter the closing float amount.'); return; }
    try {
      setCloseLoading(true);
      setCloseError('');
      await closeShift(currentShift._id || currentShift.id, {
        closingFloat: Number(closingFloat),
        variance,
      });
      setCloseDialogOpen(false);
      setClosingFloat('');
      await loadAll();
    } catch (err) {
      setCloseError(err.response?.data?.message || 'Failed to close shift.');
    } finally {
      setCloseLoading(false);
    }
  };

  /* ── cash drop ── */
  const handleCashDrop = async () => {
    if (!dropAmount || Number(dropAmount) <= 0) { setDropError('Enter a valid amount.'); return; }
    try {
      setDropLoading(true);
      setDropError('');
      await addCashDrop(currentShift._id || currentShift.id, {
        amount: Number(dropAmount),
        note: dropNote,
      });
      setDropDialogOpen(false);
      setDropAmount('');
      setDropNote('');
      await loadAll();
    } catch (err) {
      setDropError(err.response?.data?.message || 'Failed to record cash drop.');
    } finally {
      setDropLoading(false);
    }
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1e293b">Shift Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage cashier shifts, floats, and cash drops</Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadAll} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* ── Current Shift Panel ── */}
          {!currentShift ? (
            <Paper
              sx={{
                p: 5, borderRadius: 4, textAlign: 'center', mb: 4,
                background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)',
                border: '2px dashed #cbd5e1',
              }}
            >
              <AccessTime sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} color="#475569" gutterBottom>No Shift Open</Typography>
              <Typography color="text.secondary" mb={3}>Open a shift to start recording sales and transactions.</Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrow />}
                onClick={() => { setOpenError(''); setOpenFloat(''); setOpenShiftDialogOpen(true); }}
                sx={{
                  borderRadius: 3, px: 5, py: 1.5, fontWeight: 800,
                  background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                  boxShadow: '0 4px 16px rgba(34,197,94,0.4)',
                }}
              >
                Open Shift
              </Button>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ color: '#fff' }} />
                  <Typography fontWeight={800} color="#fff" fontSize={17}>Current Shift</Typography>
                  <Chip label="OPEN" size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 700, ml: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AttachMoney />}
                    onClick={() => { setDropAmount(''); setDropNote(''); setDropError(''); setDropDialogOpen(true); }}
                    sx={{ borderRadius: 2, color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    Cash Drop
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Stop />}
                    onClick={() => { setClosingFloat(''); setCloseError(''); setCloseDialogOpen(true); }}
                    sx={{ borderRadius: 2, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                  >
                    Close Shift
                  </Button>
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 2, mb: 3 }}>
                  {[
                    { label: 'Cashier', value: currentShift.cashierName || user?.name || '—' },
                    { label: 'Opened At', value: fmt(currentShift.openedAt || currentShift.createdAt) },
                    { label: 'Opening Float', value: KES(currentShift.openingFloat) },
                    { label: 'Total Sales', value: KES(currentShift.summary?.totalSales || currentShift.totalSales) },
                    { label: 'Transactions', value: currentShift.summary?.transactionCount ?? currentShift.transactionCount ?? 0 },
                    { label: 'Cash Drops', value: KES(currentShift.cashDrops?.reduce((s, d) => s + d.amount, 0) || 0) },
                  ].map((item) => (
                    <Card key={item.label} sx={{ borderRadius: 2.5, boxShadow: 'none', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <CardContent sx={{ py: '10px !important', px: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">{item.label}</Typography>
                        <Typography fontWeight={800} color="#1e293b">{item.value}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>

                {/* Cash drops list */}
                {currentShift.cashDrops?.length > 0 && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Cash Drops</Typography>
                    {currentShift.cashDrops.map((drop, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1, borderRadius: 1, bgcolor: '#f1f5f9', mb: 0.5 }}>
                        <Typography variant="body2">{fmt(drop.time || drop.createdAt)}</Typography>
                        <Typography variant="body2" color="text.secondary">{drop.note || '—'}</Typography>
                        <Typography variant="body2" fontWeight={700} color="primary">{KES(drop.amount)}</Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            </Paper>
          )}

          {/* ── Shift History Table ── */}
          <Typography variant="h6" fontWeight={700} mb={2}>Shift History</Typography>
          {shifts.length === 0 ? (
            <Alert severity="info">No shift history found.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Date', 'Cashier', 'Opening Float', 'Closing Float', 'Total Sales', 'Transactions', 'Status', 'Variance'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shifts.map((s) => {
                    const v = (s.closingFloat || 0) - (s.expectedFloat || 0);
                    const isHighVariance = Math.abs(v) > 100;
                    return (
                      <TableRow key={s._id} hover>
                        <TableCell>{fmt(s.openedAt || s.createdAt)}</TableCell>
                        <TableCell>{s.cashierName || s.cashier?.name || '—'}</TableCell>
                        <TableCell>{KES(s.openingFloat)}</TableCell>
                        <TableCell>{s.closingFloat != null ? KES(s.closingFloat) : '—'}</TableCell>
                        <TableCell>{KES(s.summary?.totalSales || s.totalSales)}</TableCell>
                        <TableCell>{s.summary?.transactionCount ?? s.transactionCount ?? 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.status === 'open' ? 'Open' : s.status === 'closed' ? 'Closed' : s.status || '—'}
                            color={s.status === 'open' ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          {s.closingFloat != null ? (
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={isHighVariance ? 'error.main' : 'success.main'}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}
                            >
                              {isHighVariance && <Warning fontSize="small" />}
                              {v >= 0 ? '+' : ''}{KES(v)}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ─── Open Shift Dialog ─── */}
      <Dialog open={openShiftDialogOpen} onClose={() => setOpenShiftDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayArrow color="success" /> Open New Shift
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label="Opening Float (KES)"
            type="number"
            value={openFloat}
            onChange={(e) => setOpenFloat(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            inputProps={{ min: 0 }}
            helperText="Cash in the till at shift start"
          />
          {openError && <Alert severity="error" sx={{ mt: 1 }}>{openError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenShiftDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleOpenShift} disabled={openLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
            {openLoading ? <CircularProgress size={18} /> : 'Start Shift'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Close Shift Dialog ─── */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Stop color="error" /> Close Shift
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="body2" color="text.secondary">Expected float (calculated)</Typography>
            <Typography fontWeight={800} fontSize={18} color="primary">{KES(expectedFloat)}</Typography>
          </Box>
          <TextField
            label="Actual Closing Float (KES)"
            type="number"
            value={closingFloat}
            onChange={(e) => setClosingFloat(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            inputProps={{ min: 0 }}
          />
          {closingFloat && (
            <Alert severity={Math.abs(variance) > 100 ? 'error' : 'success'} sx={{ py: 0.5 }}>
              Variance: {variance >= 0 ? '+' : ''}{KES(variance)}
              {Math.abs(variance) > 100 && ' — Please investigate before closing.'}
            </Alert>
          )}
          {closeError && <Alert severity="error" sx={{ mt: 1 }}>{closeError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloseDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleCloseShift} disabled={closeLoading} sx={{ borderRadius: 2 }}>
            {closeLoading ? <CircularProgress size={18} /> : 'Close Shift'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Cash Drop Dialog ─── */}
      <Dialog open={dropDialogOpen} onClose={() => setDropDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoney color="primary" /> Record Cash Drop
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Amount (KES)" type="number" value={dropAmount} onChange={(e) => setDropAmount(e.target.value)} fullWidth size="small" inputProps={{ min: 1 }} />
            <TextField label="Note / Reason" value={dropNote} onChange={(e) => setDropNote(e.target.value)} fullWidth size="small" placeholder="e.g. Safe drop, Bank deposit" />
            {dropError && <Alert severity="error">{dropError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDropDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCashDrop} disabled={dropLoading} sx={{ borderRadius: 2 }}>
            {dropLoading ? <CircularProgress size={18} /> : 'Record Drop'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

